import {memo, useCallback, useMemo} from 'react'
import {VictoryAxis, VictoryBar, VictoryChart, VictoryZoomContainer} from 'victory'

import {useIsAutodetectTimeZone} from '../../../../../../hooks/useIsAutodetectTimeZone'
import cssVariables from '../../../../../../utils/cssVariables'
import {displayDate, formatDurationWithMilliseconds} from '../../../../../../utils/dateTimeUtils'
import type {Data} from '../../TestHistoryChart.types'
import {DataTypes} from '../../TestHistoryChart.types'
import {CHART_HEIGHT, CHART_PADDING, getAxisTicks} from '../../TestHistoryChart.utils'
import type {Domain} from '../Preview/Preview.types'

import Bar from './Bar/Bar'
import type {Props} from './MainCanvas.types'

const OVERALL_CHART_HEIGHT = CHART_HEIGHT + CHART_PADDING.top + CHART_PADDING.bottom
const CHART_TOP_INDENT = 0.1
const ONE_HUNDRED = 100
const MAX_X_AXIS_WIDTH = 200
const DOMAIN_SPARE = 2
const DEPENDENT_AXIS_TICK_COUNT = 4
const tickLabelsStyles = {
  fontFamily: 'inherit',
  fontSize: cssVariables['--ring-font-size-smaller'],
  lineHeight: cssVariables['--ring-line-height-lowest'],
  fill: cssVariables['--ring-secondary-color'],
  padding: 8,
}
const axisStyles = {
  axis: {
    stroke: cssVariables['--ring-line-color'],
  },
  tickLabels: {...tickLabelsStyles, fill: cssVariables['--ring-text-color']},
}
const dependentAxisStyles = {
  axis: {
    stroke: 'transparent',
  },
  grid: {
    stroke: cssVariables['--ring-line-color'],
  },
  ticks: {
    stroke: cssVariables['--ring-line-color'],
    size: 0,
  },
  tickLabels: tickLabelsStyles,
}

function MainCanvas({
  maxDomain,
  minDomain,
  data,
  width,
  domain,
  onDomainChange,
  withAdaptScale,
}: Props) {
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const visibleBarsCount = Number(domain.x[1]) - Number(domain.x[0])
  const mainCanvasWidth = width - CHART_PADDING.left - CHART_PADDING.right
  const barWidth = parseFloat(
    (Math.floor((mainCanvasWidth / visibleBarsCount) * ONE_HUNDRED) / ONE_HUNDRED).toFixed(2),
  )
  const widthCompensation =
    mainCanvasWidth -
    parseFloat((Math.floor(barWidth * visibleBarsCount * ONE_HUNDRED) / ONE_HUNDRED).toFixed(2))
  const [visibleData, visibleDataWithSpare] = useMemo(() => {
    const visibleDataResult: Data[] = []
    const visibleDataWithSpareResult: Data[] = []
    data.forEach((item, index) => {
      if (index >= Number(domain.x[0]) && index <= Number(domain.x[1])) {
        visibleDataResult.push(item)
      }

      if (
        index >= Number(domain.x[0]) - DOMAIN_SPARE &&
        index <= Number(domain.x[1]) + DOMAIN_SPARE
      ) {
        visibleDataWithSpareResult.push(item)
      }
    })
    return [visibleDataResult, visibleDataWithSpareResult]
  }, [data, domain])
  const maxAdaptY = useMemo(() => Math.max(...visibleData.map(datum => datum.y)), [visibleData])
  const maxY = withAdaptScale ? maxAdaptY : maxDomain.y
  const ticks = useMemo(
    () => getAxisTicks(maxY * CHART_TOP_INDENT + maxY, DEPENDENT_AXIS_TICK_COUNT),
    [maxY],
  )
  const adaptMaxDomain = useMemo(
    () => ({...maxDomain, y: ticks[ticks.length - 1]}),
    [maxDomain, ticks],
  )
  const adaptDomain: Domain = useMemo(
    () => ({...domain, y: [minDomain.y, adaptMaxDomain.y]}),
    [domain, minDomain, adaptMaxDomain],
  )
  const chartPadding = useMemo(
    () => ({...CHART_PADDING, right: CHART_PADDING.right + widthCompensation}),
    [widthCompensation],
  )
  const getAxisTickFormat = useCallback(
    (index: number) => {
      const item = data[index]
      return item?.date != null
        ? displayDate(item.date, isAutodetectTimeZone, item.type === DataTypes.DEFAULT)
        : ''
    },
    [data, isAutodetectTimeZone],
  )
  const getDependentAxisTickFormat = useCallback(
    (duration: number) =>
      `${
        !duration
          ? '0ms'
          : formatDurationWithMilliseconds(duration, {
              alwaysShowMilliseconds: false,
              alwaysShowSeconds: false,
            })
      }`,
    [],
  )
  return (
    <div style={{width}}>
      <VictoryChart
        maxDomain={adaptMaxDomain}
        minDomain={minDomain}
        width={width}
        height={OVERALL_CHART_HEIGHT}
        padding={chartPadding}
        containerComponent={
          <VictoryZoomContainer
            title="Test history chart"
            zoomDimension="x"
            zoomDomain={adaptDomain}
            onZoomDomainChange={onDomainChange}
            allowZoom={false}
          />
        }
      >
        <VictoryAxis
          tickFormat={getAxisTickFormat}
          tickCount={Math.ceil(width / MAX_X_AXIS_WIDTH)}
          style={axisStyles}
          crossAxis={false}
        />
        <VictoryAxis
          tickFormat={getDependentAxisTickFormat}
          tickValues={ticks}
          style={dependentAxisStyles}
          crossAxis={false}
          dependentAxis
        />
        <VictoryBar dataComponent={<Bar barWidth={barWidth} />} data={visibleDataWithSpare} />
      </VictoryChart>
    </div>
  )
}

export default memo<Props>(MainCanvas)
